// filepath: c:\workspace\ila\frontend\src\components\controls\HookFormDatePickerField.tsx
import React, { useCallback } from "react";
import { Controller, Control } from "react-hook-form";
import { DatePicker } from "@fluentui/react-datepicker-compat";
import { Label, makeStyles, Input, Field } from "@fluentui/react-components";
import { CalendarRegular } from "@fluentui/react-icons";
import { useLocalization } from "../../hooks/useLocalization";
import { useTranslation } from "react-i18next"; // or whatever i18n hook ILA uses

interface HookFormDatePickerFieldProps {
  name: string;
  control: Control<any>;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  helperText?: string;
}

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    width: "100%",
  },
  errorMessage: {
    color: "red",
    fontSize: "12px",
  },
});

const HookFormDatePickerField: React.FC<HookFormDatePickerFieldProps> = ({
  name,
  control,
  label,
  required = false,
  disabled = false,
  placeholder = "dd.mm.yyyy",
  minDate,
  maxDate,
  helperText,
}) => {
  const styles = useStyles();
  const { i18n } = useTranslation(); // Get current language from i18n
  const currentLanguage = i18n.language || "cs"; // Fallback to Czech
  console.log("Current language:", currentLanguage);
  const dateStrings = useLocalization(currentLanguage);

  // Date formatting based on current language
  const formatDate = (date?: Date | null): string => {
    if (!date) return "";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Parse date format - based on PCT project pattern
  const parseDateFromString = useCallback((newValue: string): Date | null => {
    const trimmedValue = (newValue || "").trim();
    const parts = trimmedValue.split(".");

    // If empty string, return null
    if (parts.length === 1 && parts[0].length === 0) {
      return null;
    }

    try {
      const day =
        parts.length > 0 && parts[0].length > 0
          ? Math.max(1, Math.min(31, parseInt(parts[0], 10)))
          : 1;
      const month =
        parts.length > 1
          ? Math.max(1, Math.min(12, parseInt(parts[1], 10)))
          : 1;
      const year =
        parts.length > 2
          ? Math.max(1900, parseInt(parts[2], 10))
          : new Date().getFullYear();

      const date = new Date(year, month - 1, day);

      // Validate the date
      if (date && !isNaN(date.getTime())) {
        return date;
      }
    } catch (e) {
      // Invalid date
    }

    return null;
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        return (
          <div className={styles.container}>
            <Field
              label={label}
              required={required}
              validationMessage={fieldState.error?.message}
              validationState={fieldState.error ? "error" : "none"}
              hint={helperText}
            >
              {" "}
              <DatePicker
                value={field.value}
                appearance="underline"
                onSelectDate={(date) => {
                  field.onChange(date);
                }}
                placeholder={placeholder}
                disabled={disabled}
                disableAutoFocus={true}
                onFocus={(e) => {
                  e.preventDefault();
                }}
                onOpenChange={(open) => {
                  if (open) {
                    return;
                  }
                  if (!open && field.value !== undefined) {
                    // If the date picker is closed, we can validate the field
                    field.onBlur();
                  }
                }}
                formatDate={formatDate}
                parseDateFromString={parseDateFromString}
                allowTextInput={true}
                firstDayOfWeek={1}
                minDate={minDate}
                maxDate={maxDate}
                input={{
                  contentAfter: <CalendarRegular />,
                }}
                autoComplete="off"
                strings={dateStrings}
              />
            </Field>
          </div>
        );
      }}
    />
  );
};

export default HookFormDatePickerField;
